import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Sparkles } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLeads } from '../../context/LeadsContext';
import { apiService } from '../../services/api';
import { wsService, WebSocketMessage, NewMessageData, ConnectionStatusData } from '../../services/websocket';
import { Mensagem, Lead, Usuario, Anexo, getErrorMessage } from '../../types';
import { Chat } from '../../components/Chat/Chat';
import { LeadReativacaoSummary } from '../../components/LeadReativacaoSummary/LeadReativacaoSummary';
import { isLeadIaOrchestrationKanbanStatus } from '../../constants/leadIaOrchestrationUi';
import './LeadDetails.css';

interface LeadDetailsProps {
  leadId?: string;
  embedded?: boolean;
  onBackToDashboard?: () => void;
}

export const LeadDetails: React.FC<LeadDetailsProps> = ({
  leadId: propLeadId,
  embedded = false,
  onBackToDashboard,
}) => {
  const { leadId: routeLeadId } = useParams<{ leadId: string }>();
  const leadId = propLeadId || routeLeadId;
  const navigate = useNavigate();
  const { isAuthenticated, organizationId, usuario } = useAuth();
  const {
    getLead,
    updateLead,
    updateLeadStatus,
    updateLeadAtendente,
    deleteLead,
    registerLeadOptOut,
    leads,
    startLeadIa,
    assumeHumanLeadIa,
    stopLeadIa,
    sendLeadPriceDraft,
  } = useLeads();
  const [lead, setLead] = useState(getLead(leadId || ''));
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [loading, setLoading] = useState(true);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [selectedAtendenteId, setSelectedAtendenteId] = useState('');
  const [assigningAtendente, setAssigningAtendente] = useState(false);
  const [whatsAppAccountStatuses, setWhatsAppAccountStatuses] = useState<Record<string, 'conectado' | 'desconectado' | 'erro' | 'aguardando_qr'>>({});
  const [whatsAppStatusByPhone, setWhatsAppStatusByPhone] = useState<Record<string, 'conectado' | 'desconectado' | 'erro' | 'aguardando_qr'>>({});
  const [whatsAppAccountIdByPhone, setWhatsAppAccountIdByPhone] = useState<Record<string, string>>({});
  const [whatsAppConnectionStatus, setWhatsAppConnectionStatus] = useState<'conectado' | 'desconectado' | 'erro' | 'aguardando_qr' | 'sem_conta'>('sem_conta');
  const [reconnectingWhatsApp, setReconnectingWhatsApp] = useState(false);
  const [openingQrWhatsApp, setOpeningQrWhatsApp] = useState(false);
  const [showReconnectModal, setShowReconnectModal] = useState(false);
  const [reconnectQrCode, setReconnectQrCode] = useState<string | null>(null);
  const [lostFollowUpMonths, setLostFollowUpMonths] = useState('');
  const [syncingMessages, setSyncingMessages] = useState(false);
  const [registeringOptOut, setRegisteringOptOut] = useState(false);
  const [iaActionLoading, setIaActionLoading] = useState(false);
  const iaPollGenerationRef = useRef(0);

  useEffect(() => {
    return () => {
      iaPollGenerationRef.current += 1;
    };
  }, [leadId]);

  useEffect(() => {
    if (!leadId) return;
    const row = leads.find((l) => l.leadId === leadId);
    if (!row) return;
    setLead((prev) => {
      if (!prev || prev.leadId !== leadId) return row;
      return {
        ...prev,
        status: row.status,
        atendimentoModo: row.atendimentoModo,
        iaSessionId: row.iaSessionId,
        iaRunState: row.iaRunState,
        pendingIaDraft: row.pendingIaDraft,
        iaLastIdempotencyKey: row.iaLastIdempotencyKey,
        updatedAt: row.updatedAt,
        unreadCount: row.unreadCount,
      };
    });
  }, [leads, leadId]);

  const resolveGlobalStatus = (statuses: Array<'conectado' | 'desconectado' | 'erro' | 'aguardando_qr'>) => {
    if (statuses.length === 0) return 'sem_conta' as const;
    if (statuses.includes('conectado')) return 'conectado' as const;
    if (statuses.includes('aguardando_qr')) return 'aguardando_qr' as const;
    if (statuses.includes('desconectado')) return 'desconectado' as const;
    return 'erro' as const;
  };

  useEffect(() => {
    setWhatsAppConnectionStatus(resolveGlobalStatus(Object.values(whatsAppAccountStatuses)));
  }, [whatsAppAccountStatuses]);

  useEffect(() => {
    setSelectedAtendenteId(lead?.atendenteId || '');
  }, [lead?.atendenteId]);

  useEffect(() => {
    const persistedMonths = lead?.interessadoLigarEmMeses;
    setLostFollowUpMonths(
      typeof persistedMonths === 'number' && persistedMonths > 0
        ? String(persistedMonths)
        : ''
    );
  }, [lead?.leadId, lead?.interessadoLigarEmMeses]);

  const refreshWhatsAppStatuses = useCallback(async () => {
    const normalizePhone = (value: string) => value.replace(/\D/g, '');

    try {
      const accounts = await apiService.getWhatsAppAccounts();
      const nextStatuses: Record<string, 'conectado' | 'desconectado' | 'erro' | 'aguardando_qr'> = {};
      const nextStatusByPhone: Record<string, 'conectado' | 'desconectado' | 'erro' | 'aguardando_qr'> = {};
      const nextAccountIdByPhone: Record<string, string> = {};
      for (const account of accounts || []) {
        nextStatuses[account.accountId] = account.status;
        const normalizedPhone = normalizePhone(account.telefone || '');
        if (normalizedPhone) {
          nextStatusByPhone[normalizedPhone] = account.status;
          nextAccountIdByPhone[normalizedPhone] = account.accountId;
        }
      }
      setWhatsAppAccountStatuses(nextStatuses);
      setWhatsAppStatusByPhone(nextStatusByPhone);
      setWhatsAppAccountIdByPhone(nextAccountIdByPhone);
    } catch {
      // Fallback for profiles that can only read their own account.
      if (usuario?.whatsappAccountId) {
        try {
          const status = await apiService.getWhatsAppAccountStatus(usuario.whatsappAccountId);
          setWhatsAppAccountStatuses({
            [usuario.whatsappAccountId]: status.status as 'conectado' | 'desconectado' | 'erro' | 'aguardando_qr',
          });
          setWhatsAppStatusByPhone({});
          setWhatsAppAccountIdByPhone({});
          return;
        } catch {
          // Keep neutral state when API is unavailable.
        }
      }
      setWhatsAppAccountStatuses({});
      setWhatsAppStatusByPhone({});
      setWhatsAppAccountIdByPhone({});
    }
  }, [usuario?.whatsappAccountId]);

  useEffect(() => {
    const loadData = async () => {
      if (!leadId) return;

      try {
        setLoading(true);
        const [leadData, mensagensData] = await Promise.all([
          apiService.getLead(leadId),
          apiService.getMensagens(leadId),
        ]);
        setLead(leadData);
        setMensagens(mensagensData);

        // Load org WhatsApp connection status for chat header badge
        await refreshWhatsAppStatuses();

        // Load AI settings to check if AI is enabled
        if (organizationId) {
          try {
            const aiSettings = await apiService.getAISettings(organizationId);
            setAiEnabled(aiSettings?.enabled || false);
          } catch (error) {
            console.error('Erro ao carregar configurações de IA:', error);
            setAiEnabled(false);
          }
        }

        // Mark messages as read when opening the lead
        if (leadData.unreadCount && leadData.unreadCount > 0) {
          try {
            await apiService.markMessagesAsRead(leadId);
            // Update local state to reflect messages are now read
            setLead(prev => prev ? { ...prev, unreadCount: 0 } : prev);
          } catch (error) {
            console.error('Erro ao marcar mensagens como lidas:', error);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [leadId, organizationId, refreshWhatsAppStatuses]);

  // Fallback status sync: refresh badge periodically if WebSocket misses an event
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      refreshWhatsAppStatuses();
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, [refreshWhatsAppStatuses]);

  // Load users for atendente name lookup
  useEffect(() => {
    if (isAuthenticated) {
      apiService.getAtendentes().then(setUsuarios).catch(console.error);
    }
  }, [isAuthenticated]);

  // WebSocket: Subscribe to lead messages for real-time updates
  const handleNewMessage = useCallback((wsMessage: WebSocketMessage) => {
    if (wsMessage.type === 'new_message' && wsMessage.leadId === leadId && wsMessage.data) {
      const newMsgData = wsMessage.data as NewMessageData;
      
      // Create a Mensagem object from WebSocket data
      const ts =
        typeof newMsgData.mensagemTimestamp === 'number' && newMsgData.mensagemTimestamp > 0
          ? newMsgData.mensagemTimestamp
          : Date.parse(newMsgData.criadoEm) || Date.now();

      const newMensagem: Mensagem = {
        messageId: newMsgData.mensagemId,
        leadId: leadId!,
        timestamp: ts,
        conteudo: newMsgData.conteudo,
        direcao: newMsgData.direcao === 'recebida' ? 'inbound' : 'outbound',
        tipo: newMsgData.tipo as 'text' | 'image' | 'document' | 'audio',
        mediaUrl: newMsgData.mediaUrl,
        s3Key: newMsgData.s3Key,
        enviadoPor: newMsgData.direcao === 'recebida' ? 'whatsapp' : 'user',
        lida: false,
        createdAt: newMsgData.criadoEm,
      };

      // Check if message already exists (avoid duplicates)
      setMensagens((prev) => {
        const exists = prev.some(m => m.messageId === newMensagem.messageId);
        if (exists) {
          return prev;
        }
        return [...prev, newMensagem];
      });

      console.log('New message received via WebSocket:', newMsgData);
    }
  }, [leadId]);

  const handleConnectionStatus = useCallback((wsMessage: WebSocketMessage) => {
    const data = wsMessage.data;
    if (!data || typeof data !== 'object') return;

    const payload = data as Partial<ConnectionStatusData>;
    if (!payload.accountId || !payload.status) return;

    setWhatsAppAccountStatuses(prev => {
      return {
        ...prev,
        [payload.accountId!]: payload.status as 'conectado' | 'desconectado' | 'erro' | 'aguardando_qr',
      };
    });
  }, []);

  useEffect(() => {
    if (!leadId) return;

    // Subscribe to this lead's messages
    wsService.subscribeToLead(leadId);
    
    // Listen for new messages
    wsService.on('new_message', handleNewMessage);
    wsService.on('connection_status', handleConnectionStatus);

    // Cleanup: unsubscribe when leaving the page
    return () => {
      wsService.off('new_message', handleNewMessage);
      wsService.off('connection_status', handleConnectionStatus);
      wsService.unsubscribeFromLead();
    };
  }, [leadId, handleNewMessage, handleConnectionStatus]);

  // Fallback polling: only if WebSocket is not connected (every 30 seconds)
  useEffect(() => {
    if (!leadId) return;

    const pollMessages = async () => {
      // Only poll if WebSocket is not connected
      if (wsService.isConnected()) {
        return;
      }

      try {
        const newMensagens = await apiService.getMensagens(leadId);
        if (newMensagens.length !== mensagens.length) {
          setMensagens(newMensagens);
        }
      } catch (error) {
        console.error('Erro ao atualizar mensagens (fallback poll):', error);
      }
    };

    // Slower fallback polling (30 seconds) when WebSocket is not available
    const interval = setInterval(pollMessages, 30000);
    return () => clearInterval(interval);
  }, [leadId, mensagens.length]);

  const refreshMensagensFromApi = useCallback(async () => {
    if (!leadId) return;
    const m = await apiService.getMensagens(leadId);
    setMensagens(m);
  }, [leadId]);

  /** Em Lambda o POST /ia/start devolve cedo e a orquestração corre em async — atualiza mensagens e estado do lead até `iaRunState` deixar de ser `running`. */
  const pollLeadAndMessagesWhileIaRunning = useCallback(async () => {
    if (!leadId) return;
    const generation = ++iaPollGenerationRef.current;
    const maxTicks = 60;
    const delayMs = 1500;

    for (let tick = 0; tick < maxTicks; tick++) {
      await new Promise((r) => setTimeout(r, delayMs));
      if (generation !== iaPollGenerationRef.current) return;

      try {
        const [msgs, freshLead] = await Promise.all([
          apiService.getMensagens(leadId),
          apiService.getLead(leadId),
        ]);
        if (generation !== iaPollGenerationRef.current) return;
        setMensagens(msgs);
        setLead(freshLead);

        if (freshLead.iaRunState !== 'running') {
          break;
        }
      } catch (e) {
        console.error('Erro ao atualizar mensagens (IA assíncrona):', e);
      }
    }
  }, [leadId]);

  const handleUpdateLead = async (updates: Partial<Lead>) => {
    if (!leadId || !lead) return;

    try {
      const updated = await updateLead(leadId, updates);
      setLead(updated);
    } catch (error) {
      console.error('Erro ao atualizar lead:', error);
    }
  };

  const handleUpdateStatus = async (newStatus: Lead['status']) => {
    if (!leadId) return;

    try {
      const statusFechamento = newStatus === 'fechamento'
        ? (lead?.statusFechamento || 'em_andamento')
        : undefined;
      await updateLeadStatus(leadId, newStatus, statusFechamento);
      const updatedLead = await apiService.getLead(leadId);
      setLead(updatedLead);
    } catch (error) {
      console.error('Erro ao atualizar status do lead:', error);
      alert((error as any)?.response?.data?.message || 'Erro ao atualizar status');
    }
  };

  const handleStartLeadIaDetail = async () => {
    if (!leadId) return;
    try {
      setIaActionLoading(true);
      const updated = await startLeadIa(leadId);
      setLead(updated);
      await refreshMensagensFromApi();
      if (updated.atendimentoModo === 'ia' && updated.iaRunState === 'running') {
        void pollLeadAndMessagesWhileIaRunning();
      }
    } catch (e: any) {
      alert(getErrorMessage(e) || 'Erro ao iniciar IA');
    } finally {
      setIaActionLoading(false);
    }
  };

  const handleAssumeHumanDetail = async () => {
    if (!leadId) return;
    try {
      setIaActionLoading(true);
      const updated = await assumeHumanLeadIa(leadId);
      setLead(updated);
    } catch (e: any) {
      alert(getErrorMessage(e) || 'Erro ao assumir atendimento');
    } finally {
      setIaActionLoading(false);
    }
  };

  const handleStopLeadIaDetail = async () => {
    if (!leadId) return;
    try {
      setIaActionLoading(true);
      const updated = await stopLeadIa(leadId);
      setLead(updated);
    } catch (e: any) {
      alert(getErrorMessage(e) || 'Erro ao parar IA');
    } finally {
      setIaActionLoading(false);
    }
  };

  const handleSendPriceDraft = async () => {
    if (!leadId) return;
    try {
      setIaActionLoading(true);
      const updated = await sendLeadPriceDraft(leadId);
      setLead(updated);
      await refreshMensagensFromApi();
    } catch (e: any) {
      alert(getErrorMessage(e) || 'Erro ao enviar mensagem');
    } finally {
      setIaActionLoading(false);
    }
  };

  const handleUpdateStatusFechamento = async (newStatusFechamento: NonNullable<Lead['statusFechamento']>) => {
    if (!leadId) return;

    try {
      if (newStatusFechamento === 'nao_vendido') {
        const parsedMonths = Number(lostFollowUpMonths);
        const hasValidPersistedMonths = Number.isInteger(lead?.interessadoLigarEmMeses) && (lead?.interessadoLigarEmMeses || 0) >= 1;
        if (!hasValidPersistedMonths) {
          if (!Number.isInteger(parsedMonths) || parsedMonths < 1) {
            alert('Informe em quantos meses deseja ligar novamente antes de marcar como perdido.');
            return;
          }
          const leadWithReturnData = await updateLead(leadId, { interessadoLigarEmMeses: parsedMonths });
          setLead(leadWithReturnData);
        }
      }
      // Inativo: remarketing curto (+1 dia) no lost card — não exige meses no lead.

      const nextPosVendaStatus =
        newStatusFechamento === 'vendido'
          ? (lead?.posVendaStatus || 'conversoes_ganhas')
          : undefined;
      await updateLeadStatus(leadId, 'fechamento', newStatusFechamento, nextPosVendaStatus);
      const updatedLead = await apiService.getLead(leadId);
      setLead(updatedLead);
    } catch (error) {
      console.error('Erro ao atualizar status de fechamento:', error);
      alert((error as any)?.response?.data?.message || 'Erro ao atualizar status de fechamento');
    }
  };

  const handleMarkAsLost = async () => {
    if (!leadId) return;

    const parsedMonths = Number(lostFollowUpMonths);
    if (!Number.isInteger(parsedMonths) || parsedMonths < 1) {
      alert('Informe em quantos meses deseja ligar novamente para marcar este lead como interessado.');
      return;
    }

    try {
      const leadWithReturnData = await updateLead(leadId, { interessadoLigarEmMeses: parsedMonths });
      setLead(leadWithReturnData);
      await updateLeadStatus(leadId, 'fechamento', 'nao_vendido');
      const updatedLead = await apiService.getLead(leadId);
      setLead(updatedLead);
    } catch (error) {
      console.error('Erro ao marcar lead como interessado:', error);
      alert((error as any)?.response?.data?.message || 'Erro ao marcar lead como interessado');
    }
  };

  const handleUpdatePosVendaStatus = async (newPosVendaStatus: NonNullable<Lead['posVendaStatus']>) => {
    if (!leadId || lead?.status !== 'fechamento') return;

    try {
      await updateLeadStatus(
        leadId,
        'fechamento',
        (lead.statusFechamento || 'vendido'),
        newPosVendaStatus
      );
      const updatedLead = await apiService.getLead(leadId);
      setLead(updatedLead);
    } catch (error) {
      console.error('Erro ao atualizar status de pós-venda:', error);
      alert((error as any)?.response?.data?.message || 'Erro ao atualizar status de pós-venda');
    }
  };

  const handleAssignAtendente = async () => {
    if (!leadId || !selectedAtendenteId || selectedAtendenteId === lead?.atendenteId) return;

    try {
      setAssigningAtendente(true);
      const updatedLead = await updateLeadAtendente(leadId, selectedAtendenteId);
      setLead(updatedLead);
      alert('Atendente alterado com sucesso.');
    } catch (error) {
      console.error('Erro ao alterar atendente:', error);
      alert((error as any)?.response?.data?.message || 'Erro ao alterar atendente');
      setSelectedAtendenteId(lead?.atendenteId || '');
    } finally {
      setAssigningAtendente(false);
    }
  };

  const handleSendMensagem = async (payload: { conteudo?: string; tipo?: 'text' | 'image' | 'document' | 'audio'; mediaUrl?: string; s3Key?: string; mimeType?: string }) => {
    if (!leadId) return;

    try {
      const novaMensagem = await apiService.sendMensagem(leadId, payload);
      setMensagens((prev) => [...prev, novaMensagem]);
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      throw error;
    }
  };

  const handleAnexoAdded = async (anexo: Anexo) => {
    if (!leadId || !lead) return;
    setLead((prev) => prev ? { ...prev, anexos: [...(prev.anexos || []), anexo] } : prev);
    const isImage = anexo.mimeType?.startsWith('image/');
    try {
      await handleSendMensagem({
        tipo: isImage ? 'image' : 'document',
        s3Key: anexo.s3Key,
        conteudo: isImage ? undefined : anexo.fileName,
      });
    } catch (error) {
      console.error('Erro ao enviar documento:', error);
      window.alert((error as any)?.response?.data?.message || (error as any)?.message || 'Não foi possível enviar o arquivo via WhatsApp.');
    }
  };

  const getConnectionStatusLabel = (status: 'conectado' | 'desconectado' | 'erro' | 'aguardando_qr' | 'sem_conta') => {
    switch (status) {
      case 'conectado':
        return 'Conectado';
      case 'desconectado':
        return 'Desconectado';
      case 'aguardando_qr':
        return 'Aguardando QR';
      case 'erro':
        return 'Erro';
      default:
        return 'Sem Conta';
    }
  };

  const userLinkedWhatsAppAccountId = usuario?.whatsappAccountId || null;
  const shouldShowOwnStatus = usuario?.role === 'atendente';
  const ownAccountStatus = userLinkedWhatsAppAccountId
    ? whatsAppAccountStatuses[userLinkedWhatsAppAccountId]
    : undefined;
  const normalizePhone = (value: string | null | undefined) => (value || '').replace(/\D/g, '');
  const leadEntradaPhone = normalizePhone(lead?.whatsappNumeroEntrada);
  const leadEntryStatus = leadEntradaPhone ? whatsAppStatusByPhone[leadEntradaPhone] : undefined;
  const leadEntryAccountId = leadEntradaPhone ? whatsAppAccountIdByPhone[leadEntradaPhone] : undefined;

  const displayedConnectionStatus: 'conectado' | 'desconectado' | 'erro' | 'aguardando_qr' | 'sem_conta' =
    shouldShowOwnStatus
      ? (userLinkedWhatsAppAccountId ? ownAccountStatus || 'sem_conta' : 'sem_conta')
      : (lead?.whatsappNumeroEntrada ? leadEntryStatus || 'sem_conta' : whatsAppConnectionStatus);
  const targetReconnectAccountId = shouldShowOwnStatus ? userLinkedWhatsAppAccountId : leadEntryAccountId;

  const canReconnectOwnWhatsApp =
    !!targetReconnectAccountId && ['desconectado', 'erro'].includes(displayedConnectionStatus);
  const canOpenPendingQr =
    !!targetReconnectAccountId && displayedConnectionStatus === 'aguardando_qr';

  const handleReconnectOwnWhatsApp = async () => {
    if (!targetReconnectAccountId) {
      return;
    }

    try {
      setReconnectingWhatsApp(true);
      const response = await apiService.connectWhatsAppAccount(targetReconnectAccountId);

      if (response.qrCode) {
        setReconnectQrCode(response.qrCode);
        setShowReconnectModal(true);
      } else if (response.status === 'conectado') {
        alert('Seu WhatsApp já está conectado.');
      }
    } catch (error: any) {
      console.error('Erro ao reconectar WhatsApp do usuário:', error);
      alert(error?.response?.data?.message || 'Não foi possível iniciar a reconexão do WhatsApp.');
    } finally {
      await refreshWhatsAppStatuses();
      setReconnectingWhatsApp(false);
    }
  };

  const handleOpenPendingQr = async () => {
    if (!targetReconnectAccountId) {
      return;
    }

    try {
      setOpeningQrWhatsApp(true);
      const statusResponse = await apiService.getWhatsAppAccountStatus(targetReconnectAccountId);

      if (statusResponse.status === 'aguardando_qr' && statusResponse.qrCode) {
        setReconnectQrCode(statusResponse.qrCode);
        setShowReconnectModal(true);
        return;
      }

      const response = await apiService.connectWhatsAppAccount(targetReconnectAccountId);
      if (response.qrCode) {
        setReconnectQrCode(response.qrCode);
        setShowReconnectModal(true);
      } else {
        alert('Não foi possível abrir um QR pendente agora. Tente reconectar novamente.');
      }
    } catch (error: any) {
      console.error('Erro ao abrir QR pendente:', error);
      alert(error?.response?.data?.message || 'Não foi possível abrir o QR pendente.');
    } finally {
      await refreshWhatsAppStatuses();
      setOpeningQrWhatsApp(false);
    }
  };

  const handleDeleteLead = async () => {
    if (!leadId || !lead) return;

    const confirmDelete = window.confirm(
      `Tem certeza que deseja excluir o lead "${lead.nome}"?\n\nEsta ação irá mover o lead para a lista de excluídos.`
    );

    if (!confirmDelete) return;

    try {
      await deleteLead(leadId);
      goBackToDashboard();
    } catch (error) {
      console.error('Erro ao excluir lead:', error);
      alert('Erro ao excluir lead. Tente novamente.');
    }
  };

  const leadPhoneDigits = (lead?.telefone || '').replace(/\D/g, '');
  const canRegisterOptOut =
    !!lead && lead.status !== 'excluido' && leadPhoneDigits.length > 0;

  const handleRegisterOptOut = async () => {
    if (!leadId || !lead || !canRegisterOptOut) return;

    const confirmed = window.confirm(
      `Registrar que "${lead.nome}" pediu para não receber mais contatos?\n\n` +
        'O número será bloqueado para mensagens e campanhas (descadastro) e o lead será arquivado como excluído.'
    );
    if (!confirmed) return;

    let reasonNote: string | undefined;
    const note = window.prompt('Nota opcional (ex.: canal ou data do pedido). Deixe em branco para pular:');
    if (note && note.trim()) {
      reasonNote = note.trim();
    }

    try {
      setRegisteringOptOut(true);
      await registerLeadOptOut(leadId, reasonNote ? { reason: reasonNote } : undefined);
      goBackToDashboard();
    } catch (error) {
      console.error('Erro ao registrar descadastro:', error);
      alert((error as any)?.response?.data?.message || 'Erro ao registrar descadastro. Tente novamente.');
    } finally {
      setRegisteringOptOut(false);
    }
  };

  const handleSyncMessages = async () => {
    if (!leadId || syncingMessages) return;
    
    setSyncingMessages(true);
    try {
      const response = await apiService.syncLeadMessages(leadId);
      
      if (response.newMessages > 0) {
        alert(`${response.newMessages} mensagens sincronizadas com sucesso!`);
        
        const m = await apiService.getMensagens(leadId);
        setMensagens(m);
      } else {
        alert('Nenhuma mensagem nova encontrada. Todas as mensagens já estão sincronizadas.');
      }
    } catch (error) {
      console.error('Erro ao sincronizar mensagens:', error);
      alert('Erro ao sincronizar mensagens. Tente novamente.');
    } finally {
      setSyncingMessages(false);
    }
  };

  const handleToggleContatoRh = async () => {
    if (!lead) return;

    if (!lead.contatoRh && !lead.nomeEmpresa?.trim()) {
      window.alert('Preencha o nome da empresa antes de marcar como Contato RH.');
      return;
    }

    await handleUpdateLead({
      contatoRh: !lead.contatoRh,
      nomeEmpresa: lead.contatoRh ? undefined : lead.nomeEmpresa?.trim(),
    });
  };

  const goBackToDashboard = () => {
    if (embedded && onBackToDashboard) {
      onBackToDashboard();
      return;
    }
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="lead-details-loading">
        <div className="spinner"></div>
        <p>Carregando detalhes do lead...</p>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="lead-details-error">
        <p>Lead não encontrado</p>
        <button onClick={goBackToDashboard}>Voltar</button>
      </div>
    );
  }

  return (
    <div className={`lead-details ${embedded ? 'embedded' : ''}`}>
      {!embedded && (
        <div className="lead-details-header-wrapper">
          <header className="lead-details-header">
            <button onClick={goBackToDashboard} className="back-button">
              <span aria-hidden="true">‹</span> Voltar
            </button>
            <h1>Detalhes do Lead</h1>
          </header>
        </div>
      )}

      <div className="lead-details-content">
        <div className="lead-details-main">
          <div className="lead-info-section">
            <h2>Informações do Lead</h2>
            
            <div className="info-grid">
              <LeadReativacaoSummary
                isReativacao={lead.isReativacao}
                reativacaoDetectedAt={lead.reativacaoDetectedAt}
                reativacaoSource={lead.reativacaoSource}
                reativacaoMotivo={lead.reativacaoMotivo}
              />

              <div className="info-item">
                <label>Nome</label>
                <input
                  type="text"
                  value={lead.nome}
                  onChange={(e) => setLead(prev => prev ? { ...prev, nome: e.target.value } : prev)}
                  onBlur={(e) => handleUpdateLead({ nome: e.target.value })}
                />
              </div>

              <div className="info-item">
                <label>Telefone</label>
                <input
                  type="tel"
                  value={lead.telefone}
                  onChange={(e) => setLead(prev => prev ? { ...prev, telefone: e.target.value } : prev)}
                  onBlur={(e) => handleUpdateLead({ telefone: e.target.value })}
                />
              </div>

              <div className="info-item">
                <label>CPF</label>
                <input
                  type="text"
                  value={lead.cpf || ''}
                  onChange={(e) => setLead(prev => prev ? { ...prev, cpf: e.target.value } : prev)}
                  onBlur={(e) => handleUpdateLead({ cpf: e.target.value })}
                  placeholder="000.000.000-00"
                />
              </div>

              <div className="info-item">
                <label>WhatsApp de entrada</label>
                <input
                  type="text"
                  value={lead.whatsappNumeroEntrada || '-'}
                  readOnly
                />
              </div>

              <div className="info-item">
                <label>E-mail</label>
                <input
                  type="email"
                  value={lead.email}
                  onChange={(e) => setLead(prev => prev ? { ...prev, email: e.target.value } : prev)}
                  onBlur={(e) => handleUpdateLead({ email: e.target.value })}
                />
              </div>

              <div className="info-item">
                <label>Interessado em</label>
                <input
                  type="text"
                  value={lead.produtoInteresse || lead.iaConversationContext?.nomeCurso || ''}
                  onChange={(e) => setLead(prev => prev ? { ...prev, produtoInteresse: e.target.value } : prev)}
                  onBlur={(e) => handleUpdateLead({ produtoInteresse: e.target.value })}
                  placeholder="Ex.: Direito, Enfermagem (âncora para a IA e para o comercial)"
                />
                <small>
                  Preencha o curso, serviço ou produto. Se a IA tiver entendido o interesse na conversa, o valor pode aparecer aqui; pode editar antes de gravar.
                </small>
              </div>

              <div className="info-item">
                <label>Contato RH</label>
                <select
                  value={lead.contatoRh ? 'sim' : 'nao'}
                  onChange={(e) => {
                    const enabled = e.target.value === 'sim';
                    setLead(prev => prev ? {
                      ...prev,
                      contatoRh: enabled,
                      nomeEmpresa: enabled ? (prev.nomeEmpresa || '') : undefined,
                    } : prev);

                    if (!enabled) {
                      handleUpdateLead({ contatoRh: false, nomeEmpresa: undefined });
                    }
                  }}
                >
                  <option value="nao">Não</option>
                  <option value="sim">Sim</option>
                </select>
              </div>

              {lead.contatoRh && (
                <div className="info-item">
                  <label>Nome da Empresa</label>
                  <input
                    type="text"
                    value={lead.nomeEmpresa || ''}
                    onChange={(e) => setLead(prev => prev ? { ...prev, nomeEmpresa: e.target.value } : prev)}
                    onBlur={(e) => handleUpdateLead({ contatoRh: true, nomeEmpresa: e.target.value })}
                    placeholder="Empresa do contato RH"
                  />
                </div>
              )}

              {!(lead.status === 'fechamento' && lead.statusFechamento === 'vendido') && (
                <>
                  <div className="info-item">
                    <label>Status</label>
                    <select
                      value={lead.status}
                      onChange={(e) => handleUpdateStatus(e.target.value as Lead['status'])}
                    >
                      <option value="aguardando_contato">Aguardando Contato</option>
                      <option value="em_atendimento">Em atendimento</option>
                      <option value="em_negociacao">Em Negociacao</option>
                      <option value="urgente">Urgente</option>
                      <option value="proposta">Proposta</option>
                      <option value="fechamento">Fechamento</option>
                    </select>
                  </div>

                  {lead.status === 'fechamento' && (
                    <div className="info-item">
                      <label>Status de Fechamento</label>
                      <select
                        value={lead.statusFechamento || 'em_andamento'}
                        onChange={(e) => handleUpdateStatusFechamento(e.target.value as NonNullable<Lead['statusFechamento']>)}
                      >
                        <option value="em_andamento">Em Andamento</option>
                        <option value="vendido">Vendido</option>
                        <option value="nao_vendido">Não Vendido</option>
                        <option value="inativo">Inativo (sem resposta / funil)</option>
                      </select>
                    </div>
                  )}
                </>
              )}

              {lead &&
                !(lead.status === 'fechamento' && lead.statusFechamento === 'vendido') &&
                aiEnabled &&
                (((lead.status === 'aguardando_contato' || lead.status === 'urgente') &&
                  lead.atendimentoModo !== 'ia') ||
                  (isLeadIaOrchestrationKanbanStatus(lead.status) && lead.atendimentoModo === 'ia')) && (
                  <div className="info-item full-width">
                    <label>Atendimento IA</label>
                    <div className="lead-ia-detail-actions">
                      {(lead.status === 'aguardando_contato' || lead.status === 'urgente') && (
                        <button
                          type="button"
                          className="change-attendee-button lead-detail-ia-start"
                          onClick={handleStartLeadIaDetail}
                          disabled={iaActionLoading}
                          title="Atender com IA — consome créditos de IA"
                        >
                          {iaActionLoading ? (
                            'A processar…'
                          ) : (
                            <>
                              <Sparkles size={16} strokeWidth={2} aria-hidden className="lead-detail-ia-icon" />
                              Atender com IA
                            </>
                          )}
                        </button>
                      )}
                      {isLeadIaOrchestrationKanbanStatus(lead.status) && lead.atendimentoModo === 'ia' && (
                        <>
                          <button
                            type="button"
                            className="change-attendee-button"
                            onClick={handleAssumeHumanDetail}
                            disabled={iaActionLoading}
                          >
                            Assumir humano
                          </button>
                          {lead.iaRunState === 'running' && (
                            <button
                              type="button"
                              className="change-attendee-button"
                              onClick={handleStopLeadIaDetail}
                              disabled={iaActionLoading}
                            >
                              Parar IA
                            </button>
                          )}
                        </>
                      )}
                    </div>
                    <small>«Atender com IA» consome créditos do serviço de IA da organização.</small>
                  </div>
                )}

              {lead.pendingIaDraft?.kind === 'preco' && Boolean(lead.pendingIaDraft.conteudo?.trim()) && (
                <div className="info-item full-width pending-ia-draft-block">
                  <label>Mensagem sugerida pela IA (preço)</label>
                  <div className="pending-ia-draft-text">{lead.pendingIaDraft.conteudo}</div>
                  {lead.pendingIaDraft.fonteResumo && (
                    <small className="pending-ia-draft-source">Fonte: {lead.pendingIaDraft.fonteResumo}</small>
                  )}
                  <button
                    type="button"
                    className="change-attendee-button"
                    onClick={handleSendPriceDraft}
                    disabled={iaActionLoading}
                  >
                    {iaActionLoading ? 'A enviar…' : 'Enviar preço ao cliente (WhatsApp)'}
                  </button>
                </div>
              )}

              {lead.status === 'fechamento' && lead.statusFechamento === 'vendido' && (
                <div className="info-item">
                  <label>Status Pós-venda</label>
                  <select
                    value={lead.posVendaStatus || 'conversoes_ganhas'}
                    onChange={(e) => handleUpdatePosVendaStatus(e.target.value as NonNullable<Lead['posVendaStatus']>)}
                  >
                    <option value="conversoes_ganhas">Conversões Ganhas</option>
                    <option value="em_atendimento">Em Atendimento</option>
                    <option value="aguardando_retorno">Aguardando Retorno</option>
                    <option value="finalizado">Finalizado</option>
                  </select>
                </div>
              )}

              {isAuthenticated && (
                <div className="info-item">
                  <label>Atendente</label>
                  <select
                    value={selectedAtendenteId}
                    onChange={(e) => setSelectedAtendenteId(e.target.value)}
                    disabled={assigningAtendente || usuarios.length === 0}
                  >
                    <option value="" disabled>
                      Selecione um atendente
                    </option>
                    {usuarios.map((usuario) => (
                      <option key={usuario.usuarioId} value={usuario.usuarioId}>
                        {usuario.nome}
                      </option>
                    ))}
                  </select>
                  <button
                    className="change-attendee-button"
                    onClick={handleAssignAtendente}
                    disabled={assigningAtendente || !selectedAtendenteId || selectedAtendenteId === lead.atendenteId}
                  >
                    {assigningAtendente ? 'Alterando...' : 'Alterar Atendente'}
                  </button>
                  <small>Atualiza também a coluna inicial e o WhatsApp de entrada.</small>
                </div>
              )}
            </div>

            <div className="info-item full-width">
              <label>Observação</label>
              <textarea
                value={lead.observacao || ''}
                onChange={(e) => setLead(prev => prev ? { ...prev, observacao: e.target.value } : prev)}
                onBlur={(e) => handleUpdateLead({ observacao: e.target.value })}
                rows={4}
                placeholder="Adicione observações sobre este lead..."
              />
            </div>

            <div className="actions-section">
              <div className="lost-follow-up-group">
                <input
                  type="number"
                  min={1}
                  step={1}
                  inputMode="numeric"
                  value={lostFollowUpMonths}
                  onChange={(e) => setLostFollowUpMonths(e.target.value)}
                  placeholder="Ligar novamente em quantos meses?"
                  aria-label="Ligar novamente em quantos meses"
                />
                <button
                  type="button"
                  className="mark-lost-button"
                  onClick={handleMarkAsLost}
                  disabled={
                    lead.status === 'fechamento' &&
                    (lead.statusFechamento === 'nao_vendido' || lead.statusFechamento === 'inativo')
                  }
                >
                  {lead.status === 'fechamento' &&
                  (lead.statusFechamento === 'nao_vendido' || lead.statusFechamento === 'inativo')
                    ? 'Interessado Salvo'
                    : 'Marcar como Interessado'}
                </button>
              </div>
              {lead.contatoRh && (
                <button
                  className="register-client-button"
                  onClick={handleToggleContatoRh}
                >
                  Remover Contato RH
                </button>
              )}
              <button
                type="button"
                className="lead-opt-out-button"
                onClick={handleRegisterOptOut}
                disabled={!canRegisterOptOut || registeringOptOut}
                title={
                  !leadPhoneDigits.length
                    ? 'Cadastre um telefone no lead para usar esta ação'
                    : undefined
                }
              >
                {registeringOptOut ? 'Registrando...' : 'Cliente pediu para não contatar'}
              </button>
              <button
                className="delete-lead-button"
                onClick={handleDeleteLead}
              >
                Excluir Lead
              </button>
            </div>
          </div>

          <div className="chat-section">
            <div className="chat-section-header">
              <h2>Chat WhatsApp</h2>
              <div className="connection-status-actions">
                <button
                  className="sync-messages-button"
                  onClick={handleSyncMessages}
                  disabled={syncingMessages || whatsAppConnectionStatus !== 'conectado'}
                  title="Sincronizar mensagens do WhatsApp"
                >
                  <svg
                    className={`sync-icon ${syncingMessages ? 'spinning' : ''}`}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
                  </svg>
                  {syncingMessages ? 'Sincronizando...' : 'Sincronizar'}
                </button>
                <span className={`connection-badge status-${displayedConnectionStatus}`}>
                  {getConnectionStatusLabel(displayedConnectionStatus)}
                </span>
                {canReconnectOwnWhatsApp && (
                  <button
                    className="reconnect-badge-button"
                    onClick={handleReconnectOwnWhatsApp}
                    disabled={reconnectingWhatsApp}
                    title="Reconectar meu WhatsApp"
                  >
                    {reconnectingWhatsApp ? 'Reconectando...' : 'Reconectar'}
                  </button>
                )}
                {canOpenPendingQr && (
                  <button
                    className="reconnect-badge-button"
                    onClick={handleOpenPendingQr}
                    disabled={openingQrWhatsApp}
                    title="Abrir QR pendente"
                  >
                    {openingQrWhatsApp ? 'Abrindo...' : 'Abrir QR'}
                  </button>
                )}
              </div>
            </div>
            <Chat
              leadId={leadId!}
              mensagens={mensagens}
              anexos={lead.anexos || []}
              onSendMensagem={handleSendMensagem}
              onAnexoAdded={handleAnexoAdded}
              aiEnabled={aiEnabled}
              onRefreshMessages={refreshMensagensFromApi}
              iaOrchestrationActive={
                iaActionLoading ||
                (lead?.atendimentoModo === 'ia' && lead?.iaRunState === 'running')
              }
            />
            <div className="chat-meta-line">
              <span>R. RH: {lead.contatoRh ? 'Sim' : 'Nao'}</span>
              <span>Empresa: {lead.nomeEmpresa?.trim() || '-'}</span>
            </div>
          </div>
        </div>
      </div>

      {showReconnectModal && reconnectQrCode && (
        <div className="lead-details-modal-overlay" onClick={() => setShowReconnectModal(false)}>
          <div className="lead-details-modal lead-details-modal-qrcode" onClick={(e) => e.stopPropagation()}>
            <div className="lead-details-modal-header">
              <h3>Reconectar WhatsApp</h3>
              <button className="lead-details-modal-close" onClick={() => setShowReconnectModal(false)}>×</button>
            </div>
            <div className="lead-details-modal-body">
              <div className="lead-details-qrcode-container">
                <img src={reconnectQrCode} alt="QR Code de reconexão WhatsApp" className="lead-details-qrcode-image" />
              </div>
              <div className="lead-details-qrcode-instructions">
                <h4>Como reconectar:</h4>
                <ol>
                  <li>Abra o WhatsApp no seu celular</li>
                  <li>Toque em Menu ou Configurações</li>
                  <li>Selecione "Aparelhos conectados"</li>
                  <li>Toque em "Conectar aparelho"</li>
                  <li>Aponte a câmera para este QR Code</li>
                </ol>
              </div>
            </div>
            <div className="lead-details-modal-footer">
              <button
                className="btn btn-primary"
                onClick={async () => {
                  setShowReconnectModal(false);
                  setReconnectQrCode(null);
                  await refreshWhatsAppStatuses();
                }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
